import { useState } from 'react'
import { Rocket } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import { agentService, circleService, teamService } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import type { TeamTemplate } from '../types/team'

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: { message?: string }; message?: string } } }).response?.data
    return data?.error?.message || data?.message || 'La requête a échoué'
  }
  return error instanceof Error ? error.message : 'La requête a échoué'
}

/**
 * * Team template fields. Circle and role editing stays on the circle page.
 */
export function TeamModelEditor({ team, onClose }: { team: TeamTemplate; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(team.name)
  const [description, setDescription] = useState(team.description ?? '')
  const [notice, setNotice] = useState('')
  const [saving, setSaving] = useState(false)
  const [deployOpen, setDeployOpen] = useState(false)
  const [instanceName, setInstanceName] = useState('')
  const [picks, setPicks] = useState<Record<string, string>>({})
  const [deploying, setDeploying] = useState(false)
  const { user } = useAuthStore()

  const canvasQuery = useQuery(
    ['team-template-canvas', team.id],
    () => circleService.canvas('template', team.id),
    { enabled: deployOpen }
  )
  const agentsQuery = useQuery(
    'activeAgents',
    agentService.getActiveAgents,
    { enabled: deployOpen }
  )

  const roles = (canvasQuery.data?.circles ?? []).flatMap((circle) =>
    circle.roles
      .filter((role) => role.agentType !== 'human')
      .map((role) => ({ id: role.id, name: role.name, circle: circle.purpose }))
  )
  const instances = (agentsQuery.data ?? []).filter((agent) =>
    user?.role === 'admin' || agent.ownerId === user?.id || agent.creatorId === user?.id
  )

  const save = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setNotice('Le nom de l\'équipe est requis.')
      return
    }
    const previous = typeof team.config === 'object' && team.config !== null && !Array.isArray(team.config)
      ? { ...(team.config as Record<string, unknown>) }
      : {}
    delete previous['links']
    setSaving(true)
    setNotice('')
    try {
      await teamService.updateTeamTemplate(team.id, {
        name: trimmed,
        description,
        config: previous,
      })
      await queryClient.invalidateQueries('teamTemplates')
      await queryClient.invalidateQueries('team-templates')
      onClose()
    } catch (error) {
      setNotice(errorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const deploy = async () => {
    const assignments = Object.entries(picks)
      .filter(([, agentId]) => agentId !== '')
      .map(([sourceRoleId, agentId]) => ({ agentId, sourceRoleId }))
    setDeploying(true)
    setNotice('')
    try {
      await teamService.createTeamInstance(team.id, instanceName.trim() || undefined, assignments)
      await queryClient.invalidateQueries('teamInstances')
      setNotice('Instance d\'équipe créée.')
      setDeployOpen(false)
      setPicks({})
      setInstanceName('')
    } catch (error) {
      setNotice(errorMessage(error))
    } finally {
      setDeploying(false)
    }
  }

  return (
    <div className="card space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-primary">Équipe « {team.name} »</h2>
        <button type="button" className="button button--secondary" onClick={onClose}>Fermer</button>
      </div>
      <p className="text-sm text-secondary">
        Ces informations décrivent l'équipe, pas un cercle.
      </p>
      {notice ? <p className="text-sm text-error">{notice}</p> : null}
      <label className="block text-sm text-secondary" htmlFor={`team-edit-name-${team.id}`}>
        Nom
        <input
          id={`team-edit-name-${team.id}`}
          name="team-edit-name"
          className="form-input mt-1"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
      </label>
      <label className="block text-sm text-secondary" htmlFor={`team-edit-description-${team.id}`}>
        Description
        <textarea
          id={`team-edit-description-${team.id}`}
          name="team-edit-description"
          className="form-input mt-1"
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>
      {deployOpen ? (
        <div className="space-y-3 border-t border-gray-200 pt-4">
          <h3 className="font-medium text-primary">Nouvelle instance</h3>
          <p className="text-sm text-secondary">
            Le modèle d'équipe ne contient pas d'agent. Vous pouvez poser des instances déjà déployées sur les rôles. Une même instance peut tenir plusieurs rôles.
          </p>
          <label className="block text-sm text-secondary" htmlFor={`team-instance-name-${team.id}`}>
            Nom de l'instance
            <input
              id={`team-instance-name-${team.id}`}
              name="team-instance-name"
              className="form-input mt-1"
              value={instanceName}
              onChange={(event) => setInstanceName(event.target.value)}
              placeholder="Optionnel"
            />
          </label>
          {canvasQuery.isLoading ? <p className="text-sm text-secondary">Chargement des rôles…</p> : null}
          {roles.length === 0 && !canvasQuery.isLoading ? (
            <p className="text-sm text-secondary">Aucun rôle d'agent sur ce modèle.</p>
          ) : null}
          {roles.map((role) => (
              <label key={role.id} className="block text-sm text-secondary" htmlFor={`team-role-agent-${role.id}`}>
                {role.circle} — {role.name}
                <select
                  id={`team-role-agent-${role.id}`}
                  name={`team-role-agent-${role.id}`}
                  className="form-input mt-1"
                  value={picks[role.id] ?? ''}
                  onChange={(event) => setPicks((current) => ({ ...current, [role.id]: event.target.value }))}
                >
                  <option value="">Aucune instance</option>
                  {instances.map((agent) => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </label>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2 justify-end">
        <button type="button" className="button button--secondary" onClick={() => setDeployOpen((open) => !open)}>
          <Rocket className="w-4 h-4 mr-2" />
          {deployOpen ? 'Annuler le déploiement' : 'Déployer une instance'}
        </button>
        {deployOpen ? (
          <button type="button" className="button button--primary" onClick={() => void deploy()} disabled={deploying}>
            Créer l'instance
          </button>
        ) : null}
        <button type="button" className="button button--primary" onClick={() => void save()} disabled={saving}>
          Enregistrer
        </button>
      </div>
    </div>
  )
}
